import React, { useState, useEffect, useRef } from 'react';
import {
  ShoppingBag,
  Search,
  Menu,
  User,
  ChevronDown,
  Smartphone,
  PlusCircle,
  ShieldCheck,
  MessageCircle,
  LayoutDashboard,
  Store,
  Package,

  Heart,
  Settings,
  HelpCircle,
  LogOut,
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';


import { logout } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useListing } from '../context/ListingContext';
import { chatService } from '../services/chatService';
import { MobileMenu } from './MobileMenu';
import { CountrySelector } from './CountrySelector';
import { MobileSearchBar } from './MobileSearchBar';

export function Navbar() {
  const [isHubsDropdownOpen, setIsHubsDropdownOpen] = useState(false);
  const [isCountyDropdownOpen, setIsCountyDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, profile, isAdmin, isSupport, isFounder, openAuthModal } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedHub, setSelectedHub] = useState('all');
  const [selectedCounty, setSelectedCounty] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { openPostListingModal } = useListing();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const countyDropdownRef = useRef<HTMLDivElement>(null);
  const navbarRef = useRef<HTMLDivElement>(null);
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [navUnreadMessages, setNavUnreadMessages] = useState(0);

  // Subscribe to unread messages for notification badge
  useEffect(() => {
    if (!user) {
      setNavUnreadMessages(0);
      return;
    }
    const unsub = chatService.subscribeToUnreadCount(user.uid, setNavUnreadMessages);
    return () => unsub();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsHubsDropdownOpen(false);
      }
      if (countyDropdownRef.current && !countyDropdownRef.current.contains(event.target as Node)) {
        setIsCountyDropdownOpen(false);
      }
      if (navbarRef.current && !navbarRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target as Node)
      ) {
        setIsProfileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);


  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set('q', searchQuery.trim());
    if (selectedHub !== 'all') params.set('hub', selectedHub);
    if (selectedCounty && selectedCounty !== 'all') params.set('county', selectedCounty);
    if (params.toString()) {
      navigate(`/search?${params.toString()}`);
    } else {
      navigate(`/search`);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed', error);
    }
  };

  const mainHubs = [
    { id: 'hotels', name: 'Hotels', path: '/hub/hotels' },
    { id: 'logistics', name: 'Logistics', path: '/hub/logistics' },
    { id: 'retailers', name: 'Retailers', path: '/hub/retailers' },
    { id: 'wholesalers', name: 'Wholesalers', path: '/hub/wholesalers' },
    { id: 'secondhand', name: 'Secondhand', path: '/hub/secondhand' },
    { id: 'farmer', name: 'Farmer', path: '/hub/farmer' },
    { id: 'digital', name: 'Digital', path: '/hub/digital' },
    { id: 'services', name: 'Services', path: '/hub/services' },
    { id: 'tech', name: 'Tech', path: '/hub/tech' },
    { id: 'live', name: 'Pambo Live', path: '/live' },
  ];

  return (
    <div ref={navbarRef} className="w-full sticky top-0 bg-white shadow-sm flex flex-col z-[1001]">
      {/* Top Bar */}
      <div className="bg-gray-50 border-b border-gray-100 text-[11px] text-slate-500 py-1.5 hidden md:block">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex space-x-6 items-center">
            <Link
              to="/"
              className="text-[#FF6600] font-black hover:text-orange-700 flex items-center gap-1.5 uppercase tracking-wider"
            >
              <ShoppingBag className="w-3.5 h-3.5" />
              Pambo Classifieds
            </Link>
          </div>
          <div className="flex space-x-6 items-center">
            <CountrySelector />
            <span className="flex items-center text-slate-600 font-medium">
              <Smartphone className="w-3.5 h-3.5 mr-1.5" />
              Get Pambo App
            </span>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className="bg-white border-b border-gray-50 py-3 relative z-[100]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 lg:h-20 gap-4 relative">
            
            {/* Mobile Left: Menu Toggle */}
            <div className="flex md:hidden items-center shrink-0 z-10 w-1/3 justify-start">
              <button 
                onClick={() => setIsMobileMenuOpen(true)}
                className="p-2 -ml-2 text-slate-600 hover:text-orange-500 transition-colors"
                aria-label="Open Menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>

            {/* Mobile Center: Logo (Absolutely positioned for perfect centering) */}
            <div className="flex md:hidden items-center justify-center absolute left-1/2 -translate-x-1/2 z-10 w-1/3">
              <Link to="/" className="flex items-center pointer-events-auto">
                <img
                  src="/pambo-official-logo-orange-cropped.png"
                  alt="Pambo Marketplace"
                  style={{ height: '36px', width: 'auto', objectFit: 'contain' }}
                />
              </Link>
            </div>

            {/* Desktop Left: Logo */}
            <div className="hidden md:flex items-center flex-shrink-0 z-10">
              <Link to="/" className="flex items-center">
                <img
                  src="/pambo-official-logo-orange-cropped.png"
                  alt="Pambo Marketplace"
                  style={{ height: '48px', width: 'auto', objectFit: 'contain' }}
                />
              </Link>
            </div>

            {/* Premium Search Bar */}
            <div className="hidden md:flex flex-1 max-w-2xl mx-10">
              <form
                onSubmit={handleSearch}
                className="flex w-full rounded-full overflow-hidden border-2 border-[#FF6600] transition-all bg-white shadow-lg shadow-orange-500/5"
              >

                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-6 pr-3 py-3.5 text-sm bg-transparent placeholder-gray-400 focus:outline-none font-medium"
                  placeholder="What are you looking for today?"
                />
                <button
                  type="submit"
                  className="bg-[#FF6600] hover:bg-[#E65C00] text-white px-10 py-3.5 text-sm font-black uppercase tracking-wider transition-all flex items-center gap-2 active:scale-95"
                >
                  <Search className="h-4 w-4 stroke-[3px]" />
                  Search
                </button>
              </form>
            </div>

            <div className="flex items-center gap-2 md:gap-5 shrink-0 z-10 justify-end">
              <button
                onClick={openPostListingModal}
                className="hidden md:flex items-center justify-center w-8 h-8 md:w-auto md:px-6 md:py-2.5 bg-[#FF6600] hover:bg-[#E65C00] text-white text-xs font-black rounded-full transition-all shadow-lg shadow-orange-500/20 active:scale-95 uppercase tracking-widest"
              >
                <PlusCircle className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Post Ads</span>
              </button>

              {user ? (
                <div className="flex items-center gap-4">
                  {/* Message Notification Icon */}
                  <Link
                    to="/dashboard?tab=messages"
                    className="relative hidden md:flex flex-col items-center text-slate-400 hover:text-orange-600 transition-colors group"
                    title="Messages"
                  >
                    <MessageCircle className="h-5 w-5 group-hover:scale-110 transition-transform" />
                    <span className="text-[10px] font-bold uppercase tracking-tighter hidden md:block">
                      Messages
                    </span>
                    {navUnreadMessages > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-black rounded-full min-w-[16px] h-4 flex items-center justify-center px-1 shadow-sm border border-white">
                        {navUnreadMessages > 99 ? '99+' : navUnreadMessages}
                      </span>
                    )}
                  </Link>
                  {(isAdmin || isSupport || isFounder) && (
                    <Link
                      to="/admin"
                      className="hidden md:flex flex-col items-center text-slate-400 hover:text-purple-600 transition-colors group"
                    >
                      <ShieldCheck className="h-5 w-5 mb-0.5 group-hover:scale-110 transition-transform" />
                      <span className="text-[10px] font-bold uppercase tracking-tighter">
                        Admin
                      </span>
                    </Link>
                  )}
                  <div className="relative block" ref={profileDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                      className="flex items-center gap-2 md:gap-3 group pl-2 md:border-l border-gray-100"
                    >
                      <div className="relative">
                        {profile?.photoURL || user.photoURL ? (
                          <img
                            src={profile?.photoURL || user.photoURL || ''}
                            alt="Profile"
                            className="w-9 h-9 rounded-xl border-2 border-gray-100 group-hover:border-orange-500 transition-all shadow-sm"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-xl bg-orange-100 text-[#FF5722] flex items-center justify-center font-black group-hover:bg-orange-200 transition-colors shadow-sm">
                            {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
                          </div>
                        )}
                        <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full"></div>
                      </div>
                      <div className="hidden md:block flex-col max-w-[120px] text-left">
                        <span className="text-[10px] text-slate-400 font-bold block leading-none uppercase tracking-tighter mb-0.5">
                          {isAdmin || isFounder ? 'Admin Account' : 'Seller Account'}
                        </span>
                        <span className="text-sm font-black text-slate-800 block leading-tight truncate group-hover:text-orange-600 transition-colors flex items-center gap-1">
                          {profile?.displayName || user.displayName || 'My Shop'}
                          <ChevronDown
                            className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isProfileDropdownOpen ? 'rotate-180' : ''}`}
                          />
                        </span>
                      </div>
                    </button>

                    {isProfileDropdownOpen && (
                      <div className="absolute top-full right-0 mt-3 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-[2000] animate-in fade-in slide-in-from-top-2 duration-200">
                        {/* User Header */}
                        <div className="px-4 pb-2 border-b border-gray-50 mb-1">
                          <p className="text-xs font-black text-slate-800 truncate">
                            {profile?.displayName || user.displayName || 'Seller Account'}
                          </p>
                          <p className="text-[10px] font-medium text-slate-500 truncate">
                            {user.email}
                          </p>
                        </div>

                        {/* Group 1: Core Profile */}
                        <div className="py-1">
                          {(isAdmin || isSupport || isFounder) && (
                            <Link
                              to="/admin"
                              className="flex items-center gap-3 px-4 py-1.5 text-xs font-bold text-purple-600 hover:bg-purple-50 transition-colors"
                              onClick={() => setIsProfileDropdownOpen(false)}
                            >
                              <ShieldCheck className="w-4 h-4 text-purple-500" /> Admin Dashboard
                            </Link>
                          )}
                          <Link
                            to="/dashboard?tab=profile"
                            className="flex items-center gap-3 px-4 py-1.5 text-xs font-bold text-slate-600 hover:bg-orange-50 hover:text-[#FF6600] transition-colors"
                            onClick={() => setIsProfileDropdownOpen(false)}
                          >
                            <User className="w-4 h-4 text-slate-400" /> Profile
                          </Link>
                          <Link
                            to="/dashboard"
                            className="flex items-center gap-3 px-4 py-1.5 text-xs font-bold text-slate-600 hover:bg-orange-50 hover:text-[#FF6600] transition-colors"
                            onClick={() => setIsProfileDropdownOpen(false)}
                          >
                            <LayoutDashboard className="w-4 h-4 text-slate-400" /> Dashboard
                          </Link>
                          <Link
                            to={(user as any)?.storeSlug ? `/shop/${(user as any).storeSlug}` : `/seller/${user.uid}`}
                            className="flex items-center gap-3 px-4 py-1.5 text-xs font-bold text-slate-600 hover:bg-orange-50 hover:text-[#FF6600] transition-colors"
                            onClick={() => setIsProfileDropdownOpen(false)}
                          >
                            <Store className="w-4 h-4 text-slate-400" /> My Shop
                          </Link>
                          <Link
                            to="/dashboard?tab=listings"
                            className="flex items-center gap-3 px-4 py-1.5 text-xs font-bold text-slate-600 hover:bg-orange-50 hover:text-[#FF6600] transition-colors"
                            onClick={() => setIsProfileDropdownOpen(false)}
                          >
                            <Package className="w-4 h-4 text-slate-400" /> My Ads
                          </Link>
                        </div>

                        <div className="h-px bg-gray-50 my-1"></div>

                        {/* Group 2: Engagement */}
                        <div className="py-1">
                          <Link
                            to="/messages"
                            className="flex items-center gap-3 px-4 py-1.5 text-xs font-bold text-slate-600 hover:bg-orange-50 hover:text-[#FF6600] transition-colors"
                            onClick={() => setIsProfileDropdownOpen(false)}
                          >
                            <MessageCircle className="w-4 h-4 text-slate-400" /> Messages
                          </Link>

                          <Link
                            to="/saved"
                            className="flex items-center gap-3 px-4 py-1.5 text-xs font-bold text-slate-600 hover:bg-orange-50 hover:text-[#FF6600] transition-colors"
                            onClick={() => setIsProfileDropdownOpen(false)}
                          >
                            <Heart className="w-4 h-4 text-slate-400" /> Saved Ads
                          </Link>
                        </div>

                        <div className="h-px bg-gray-50 my-1"></div>

                        {/* Group 3: App Settings */}
                        <div className="py-1">
                          <Link
                            to="/settings"
                            className="flex items-center gap-3 px-4 py-1.5 text-xs font-bold text-slate-600 hover:bg-orange-50 hover:text-[#FF6600] transition-colors"
                            onClick={() => setIsProfileDropdownOpen(false)}
                          >
                            <Settings className="w-4 h-4 text-slate-400" /> Settings
                          </Link>
                          <Link
                            to="/help-center"
                            className="flex items-center gap-3 px-4 py-1.5 text-xs font-bold text-slate-600 hover:bg-orange-50 hover:text-[#FF6600] transition-colors"
                            onClick={() => setIsProfileDropdownOpen(false)}
                          >
                            <HelpCircle className="w-4 h-4 text-slate-400" /> Help
                          </Link>
                        </div>

                        <div className="h-px bg-gray-50 my-1"></div>

                        {/* Group 4: Logout */}
                        <div className="py-1">
                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              handleLogout();
                            }}
                            className="w-full text-left flex items-center gap-3 px-4 py-1.5 text-xs font-bold text-red-500 hover:bg-red-50 transition-colors"
                          >
                            <LogOut className="w-4 h-4 text-red-400" /> Log Out
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 md:gap-3">
                  <button
                    onClick={() => openAuthModal('login')}
                    className="flex items-center gap-1.5 md:gap-2 group px-2 py-1.5 md:px-4 md:py-2 rounded-xl text-[#FF6600] bg-orange-50 hover:bg-orange-100 transition-all font-bold shrink-0"
                  >
                    <div className="hidden sm:flex w-6 h-6 md:w-8 md:h-8 rounded-lg bg-white/60 items-center justify-center group-hover:bg-white transition-colors">
                      <User className="h-3.5 w-3.5 md:h-4 md:w-4" />
                    </div>
                    <span className="text-[12px] md:text-sm">Sign In</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <MobileMenu
          handleLogout={handleLogout}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleSearch={handleSearch}
          mainHubs={mainHubs}
        />
      )}

      {/* Categories Sub-nav - Fills the "Empty" space */}
      <div className="bg-white border-b border-gray-100 hidden">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-1">
            <div className="flex items-center space-x-2">
              <div className="flex items-center gap-2 px-4 py-2.5 text-xs font-black text-slate-800 cursor-pointer hover:text-[#FF6600] transition-colors uppercase tracking-tight mr-4">
                <Menu className="w-4 h-4" />
                All Categories
              </div>
              {mainHubs.map((hub) => (
                <Link
                  key={hub.id}
                  to={hub.path}
                  className="px-4 py-2.5 text-[11px] font-black text-slate-500 hover:text-[#FF6600] transition-all uppercase tracking-tight flex items-center gap-2"
                >
                  {hub.name}
                  {hub.id === 'live' && (
                    <span className="flex h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse"></span>
                  )}
                </Link>
              ))}
            </div>
            <div className="flex items-center gap-6">
              <Link
                to="/help-center"
                className="text-[11px] font-bold text-slate-400 hover:text-[#FF6600] transition-colors uppercase tracking-tighter"
              >
                Trade Assurance
              </Link>
              <Link
                to="/post-ad"
                className="text-[11px] font-black text-[#FF6600] hover:text-orange-700 transition-colors uppercase tracking-tighter border-l border-gray-100 pl-6"
              >
                Supplier Membership
              </Link>
            </div>
          </div>
        </div>
      </div>

      <MobileSearchBar />
    </div>
  );
}
